import React, { useContext, useMemo } from "react";
import KPICard from "./components/KPICard";
import HeatmapChart from "./components/HeatmapChart";
import DispositionBreakdown from "./components/DispositionBreakdown";
import Image from "../../Tags/Image";
import SharedDataContext from "../../../SharedDataContext";
import { useSmartQuery } from "../../../apiHooks/useSmartQuery";
import { getCallActivityOverview } from "../../../apis/callActivity";
import { useStoreCallActivity } from "../../../stores/useStoreCallActivity";
import { useTheme } from "../../ThemeProvider";

export default function ActivityTab({ filters, hasAccess = true }) {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { overviewData, setOverviewData } = useStoreCallActivity();
  const { theme } = useTheme();

  const getUserId = () => {
    if (filters.viewToggle === "Individuals") {
      return filters.userTeamSelector;
    }
    return null;
  };

  const getTeamId = () => {
    if (filters.viewToggle === "Team") {
      return filters.userTeamSelector;
    }
    return null;
  };

  const userId = getUserId();
  const teamId = getTeamId();

  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (filters.dateRange) {
      if (filters.dateRange === "custom") {
        params.append("time_range", "custom");
        if (filters.customStartDate) {
          params.append("start_date", filters.customStartDate);
        }
        if (filters.customEndDate) {
          params.append("end_date", filters.customEndDate);
        }
      } else {
        const dateRangeMap = {
          "7 days": "7d",
          "30 days": "30d",
          "60 days": "60d",
          "90 days": "90d",
          "180 days": "180d",
          "1 year": "1y",
        };
        params.append("time_range", dateRangeMap[filters.dateRange] || "30d");
      }
    }

    if (filters.callDirection && filters.callDirection !== "All") {
      const callDirectionMap = {
        outbound: "Outgoing",
        inbound: "Incoming",
        Outgoing: "Outgoing",
        Incoming: "Incoming",
      };
      params.append(
        "call_direction",
        callDirectionMap[filters.callDirection] || filters.callDirection.toLowerCase()
      );
    }

    return params.toString();
  };

  const queryParams = buildQueryParams();

  const { isLoading, error } = useSmartQuery(
    ["call_activity_overview", activeWorkspace?.id, userId, teamId, queryParams],
    () =>
      getCallActivityOverview({
        userId,
        teamId,
        query: queryParams,
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id && hasAccess,
      zustandSetter: setOverviewData,
    }
  );

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const getTrend = (changePercent) => {
    if (changePercent > 0) return { trend: "up", value: `${changePercent}%` };
    if (changePercent < 0) return { trend: "down", value: `${Math.abs(changePercent)}%` };
    return { trend: "neutral", value: "0%" };
  };

  const kpis = overviewData?.data?.kpis;
  const heatmapData = overviewData?.data?.heatmap;
  const dispositionData = overviewData?.data?.disposition;
  const trendsData = overviewData?.data?.trends;

  const kpiData = useMemo(() => {
    if (!kpis) {
      return [
        {
          title: "Total calls",
          value: isLoading ? "..." : "0",
          subtitle: "Last 30 days",
          icon: (
            <Image
              src={
                theme === "dark"
                  ? "/images/icons/untitledui/call-activity/total-calls-dark.svg"
                  : "/images/icons/untitledui/call-activity/Total-calls.svg"
              }
              alt="Total calls"
              className="no-invert"
            />
          ),
          trend: "neutral",
          trendValue: "0%",
        },
        {
          title: "Average call duration",
          value: isLoading ? "..." : "0s",
          subtitle: "Average per call",
          icon: (
            <Image
              src={
                theme === "dark"
                  ? "/images/icons/untitledui/call-activity/avg-call-duration-dark.svg"
                  : "/images/icons/untitledui/call-activity/average-call-duration.svg"
              }
              alt="Average call duration"
              className="no-invert"
            />
          ),
          trend: "neutral",
          trendValue: "0%",
        },
        {
          title: "Connect rate",
          value: isLoading ? "..." : "0%",
          subtitle: "Successful connections",
          icon: (
            <Image
              src={
                theme === "dark"
                  ? "/images/icons/untitledui/call-activity/connect-rate-dark.svg"
                  : "/images/icons/untitledui/call-activity/Connect-rate.svg"
              }
              alt="Connect rate"
              className="no-invert"
            />
          ),
          trend: "neutral",
          trendValue: "0%",
        },
        {
          title: "Unanswered rate",
          value: isLoading ? "..." : "0%",
          subtitle: "No response calls",
          icon: (
            <Image
              src={
                theme === "dark"
                  ? "/images/icons/untitledui/call-activity/unanswered-rate-dark.svg"
                  : "/images/icons/untitledui/call-activity/unanswered-rate.svg"
              }
              alt="Unanswered rate"
              className="no-invert"
            />
          ),
          trend: "neutral",
          trendValue: "0%",
        },
      ];
    }

    const totalCallsTrend = getTrend(kpis.total_calls_change_percent);
    const avgDurationTrend = getTrend(kpis.avg_duration_change_percent);

    return [
      {
        title: "Total calls",
        value: formatNumber(kpis.total_calls),
        subtitle: "Last 30 days",
        icon: (
          <Image
            src={
              theme === "dark"
                ? "/images/icons/untitledui/call-activity/total-calls-dark.svg"
                : "/images/icons/untitledui/call-activity/Total-calls.svg"
            }
            alt="Total calls"
            className="no-invert"
          />
        ),
        trend: totalCallsTrend.trend,
        trendValue: totalCallsTrend.value,
      },
      {
        title: "Average call duration",
        value: formatDuration(kpis.avg_duration_seconds),
        subtitle: "Average per call",
        icon: (
          <Image
            src={
              theme === "dark"
                ? "/images/icons/untitledui/call-activity/avg-call-duration-dark.svg"
                : "/images/icons/untitledui/call-activity/average-call-duration.svg"
            }
            alt="Average call duration"
            className="no-invert"
          />
        ),
        trend: avgDurationTrend.trend,
        trendValue: avgDurationTrend.value,
      },
      {
        title: "Connect rate",
        value: `${kpis.connect_rate_percent.toFixed(1)}%`,
        subtitle: "Successful connections",
        icon: (
          <Image
            src={
              theme === "dark"
                ? "/images/icons/untitledui/call-activity/connect-rate-dark.svg"
                : "/images/icons/untitledui/call-activity/Connect-rate.svg"
            }
            alt="Connect rate"
            className="no-invert"
          />
        ),
        trend: "neutral",
        trendValue: "0%",
      },
      {
        title: "Unanswered rate",
        value: `${kpis.unanswered_rate_percent.toFixed(1)}%`,
        subtitle: "No response calls",
        icon: (
          <Image
            src={
              theme === "dark"
                ? "/images/icons/untitledui/call-activity/unanswered-rate-dark.svg"
                : "/images/icons/untitledui/call-activity/unanswered-rate.svg"
            }
            alt="Unanswered rate"
            className="no-invert"
          />
        ),
        trend: "neutral", // API doesn't provide change for this
        trendValue: "0%",
      },
    ];
  }, [kpis, isLoading, theme]);
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <p className="text-red-600">Error loading call activity data: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {kpiData.map((kpi, index) => (
          <KPICard
            key={index}
            title={kpi.title}
            value={kpi.value}
            subtitle={kpi.subtitle}
            icon={kpi.icon}
            trend={kpi.trend}
            trendValue={kpi.trendValue}
          />
        ))}
      </div>
      <HeatmapChart heatmapData={heatmapData} isLoading={isLoading} />
      <DispositionBreakdown dispositionData={dispositionData} trendsData={trendsData} isLoading={isLoading} />
    </div>
  );
}
